import { AuditLogEntry } from '../identityProviderManagement.types';

export const DETAIL_KEY_PREFIX = 'settings.authentication.audit.detailKeys';
export const ACTION_TYPE_PREFIX = 'settings.authentication.audit.actionTypes';

/**
 * `occurredAt` is epoch seconds; Date takes milliseconds. Getting this wrong
 * renders every row as 1970 without failing anything.
 */
export const formatOccurredAt = (occurredAt: number, locale: string): string =>
    new Date(occurredAt * 1000).toLocaleString(locale);

export interface DetailPair {
    key: string;
    label: string;
    value: string;
}

/**
 * The details pairs, sorted by key so every entry of one type lists them in
 * the same order, whatever the language.
 *
 * `idpDisplayName` is deliberately NOT folded in here -- it names which
 * provider the change was about, which is column-worthy rather than a
 * fragment of a run-on string a screen reader cannot take apart.
 *
 * `details` is an open map -- the service may add keys without a contract
 * change -- so an unrecognised key is labelled with the key itself rather than
 * dropped. Dropping it would silently shrink an audit record.
 */
export const detailPairs = (
    entry: AuditLogEntry,
    translateKey: (key: string) => string,
): DetailPair[] =>
    Object.entries(entry.details ?? {})
        .sort(([a], [b]) => a.localeCompare(b, 'en'))
        .map(([key, value]) => ({
            key,
            label: translateKey(`${DETAIL_KEY_PREFIX}.${key}`),
            value: String(value),
        }));

/** The Details cell: the pairs on one line. */
export const describeDetails = (pairs: DetailPair[]): string =>
    pairs.map(({ label, value }) => `${label}: ${value}`).join('; ');
